import React, { useState } from 'react';
import { Table, Alert } from 'reactstrap';
import {
  formatIsoDate,
  subDays,
  MEASUREMENT_TYPES,
  useTranslator,
  formatMediumDate,
  formatMeasurementUnit,
} from '@front-activerse/common-components';
import DateLineChartPlaceholder from '../../../Layout/Chart/DateLineChartPlaceholder';
import MeasurementHistoryFilters from './MeasurementHistoryFilters';
import MeasurementHistoryChart from './MeasurementHistoryChart';
import { useNavigate } from 'react-router-dom';
import { IMeasurement } from '../../types';

interface IMeasurementHistoryContainerProps {
  load: (measurementType: string, periodLength: number, periodEnd: Date) => void;
  loading: boolean;
  history?: IMeasurement[];
  goalWeight?: {
    value: number;
    unit: string;
  };
  measurementType: string;
  periodLength: number;
  periodEnd: Date;
  availableMeasurements: string[][];
  pathsMeasurementEdit: string;
}

const MeasurementHistoryContainer: React.FC<IMeasurementHistoryContainerProps> = ({
  load,
  loading,
  history = undefined,
  goalWeight = undefined,
  measurementType,
  periodLength,
  periodEnd,
  availableMeasurements,
  pathsMeasurementEdit,
}) => {
  const { t } = useTranslator();
  const navigate = useNavigate();

  const [localMeasurementType, setLocalMeasurementType] = useState(measurementType);
  const [localPeriodLength, setLocalPeriodLength] = useState(periodLength);

  const handleMeasurementTypeChange = (measurementType: string) => {
    setLocalMeasurementType(measurementType);
    load(measurementType, localPeriodLength, periodEnd);
  };

  const handlePeriodLengthChange = (periodLength: number) => {
    setLocalPeriodLength(periodLength);
    load(localMeasurementType, periodLength, periodEnd);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <>
          <DateLineChartPlaceholder />
          <Table>
            <tbody>
              {Array.from(Array(5)).map((data, key) => (
                <tr key={key}>
                  <th className="placeholder"></th>
                  <td className="placeholder"></td>
                  <td className="placeholder"></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      );
    }

    if (history && history.length) {
      const period = {
        start: formatIsoDate(subDays(periodEnd, localPeriodLength - 1)),
        end: formatIsoDate(periodEnd),
      };

      let trend = undefined;
      if (
        localMeasurementType === MEASUREMENT_TYPES.WEIGHT &&
        goalWeight &&
        goalWeight.value != null
      ) {
        trend = goalWeight.value;
      }

      const renderChange = (change: number | undefined, unit: string | undefined) => {
        if (change && change < 0) {
          return (
            <span className="positive-change">
              {change} {unit}
            </span>
          );
        } else if (change === 0) {
          return <span className="neutral-change">-</span>;
        } else if (change && change > 0) {
          return (
            <span className="negative-change">
              +{change} {unit}
            </span>
          );
        }
      };

      return (
        <>
          <MeasurementHistoryChart history={history} period={period} trend={trend} />
          <Table>
            <tbody>
              {history.map((measurement) => (
                <tr
                  key={measurement.date}
                  onClick={() =>
                    navigate(`${pathsMeasurementEdit.split('/:')[0]}/${measurement.date}`)
                  }
                  className="cursor-pointer"
                >
                  <th>{formatMediumDate(measurement.date)}</th>
                  <td>
                    <span className="featured">
                      {measurement.value} {formatMeasurementUnit(measurement.unit)}
                    </span>
                  </td>
                  <td className="text-end">
                    {renderChange(measurement.change, formatMeasurementUnit(measurement.unit))}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      );
    }

    return (
      <Alert color="warning" className="mb-0">
        <p className="small">
          {t('measurements.noMeasurementInSelectedPeriod.notification.content')}
        </p>
      </Alert>
    );
  };

  return (
    <div className="pt-l">
      <h2 className="title-medium semi-bold mb-m">{t('measurements.measurementsHistory.title')}</h2>
      <div className="white-box">
        <MeasurementHistoryFilters
          measurementType={localMeasurementType}
          periodLength={localPeriodLength}
          handlePeriodLengthChange={handlePeriodLengthChange}
          handleMeasurementTypeChange={handleMeasurementTypeChange}
          availableMeasurements={availableMeasurements}
        />
        {renderContent()}
      </div>
    </div>
  );
};

export default MeasurementHistoryContainer;
